import React from 'react';
import { Col, Typography } from 'antd';
import {
  WarningOutlined,
  ClockCircleOutlined,
  ExceptionOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import type { QualityDashboardActionSummary } from '@/services/qualityDashboard.service';

const { Text } = Typography;

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const QmsOutlookRibbon: React.FC<{
  summary: QualityDashboardActionSummary;
}> = ({ summary }) => {
  const navigate = useNavigate();
  const ncr30 = summary.open_ncr_aging?.['30+'] ?? 0;
  const capa30 = summary.overdue_capa_aging?.['30+'] ?? 0;

  return (
    <div id="outlook">
      <ControlTowerMetricStrip zone="metricsOrders" title="Outlook (forward look)">
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Open NCRs"
            count={summary.open_ncr_count}
            icon={<WarningOutlined />}
            urgency={
              summary.critical_ncr_count > 0
                ? 'critical'
                : summary.open_ncr_count > 0
                  ? 'attention'
                  : 'neutral'
            }
            extra={
              ncr30 > 0 || summary.critical_ncr_count > 0 ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Critical {summary.critical_ncr_count} · 30+ {ncr30}
                </Text>
              ) : undefined
            }
            footerLink={{
              label: 'Follow up',
              onClick: () => scrollTo('ncr-capa-follow-up'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Overdue CAPAs"
            count={summary.overdue_capa_count}
            icon={<ExceptionOutlined />}
            urgency={summary.overdue_capa_count > 0 ? 'critical' : 'neutral'}
            extra={
              capa30 > 0 ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {capa30} aged 30+
                </Text>
              ) : undefined
            }
            footerLink={{
              label: 'Follow up',
              onClick: () => scrollTo('ncr-capa-follow-up'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Overdue inspections"
            count={summary.overdue_inspection_count}
            icon={<ClockCircleOutlined />}
            urgency={summary.overdue_inspection_count > 0 ? 'critical' : 'neutral'}
            footerLink={{
              label: 'Queue',
              onClick: () => scrollTo('ncr-capa-follow-up'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="WO gates waiting"
            count={summary.wo_gates_waiting_count}
            icon={<ToolOutlined />}
            urgency={summary.wo_gates_waiting_count > 0 ? 'attention' : 'neutral'}
            extra={
              <Text type="secondary" style={{ fontSize: 11 }}>
                IIR {summary.pending_iir_count} · Stage {summary.pending_stage_count} · FIR{' '}
                {summary.pending_fir_count}
              </Text>
            }
            footerLink={{
              label: 'Gates',
              onClick: () => scrollTo('wo-inspection-gates'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Approvals waiting"
            count={summary.approvals_waiting_count}
            icon={<ExceptionOutlined />}
            urgency={summary.approvals_waiting_count > 0 ? 'attention' : 'neutral'}
            extra={
              <Text type="secondary" style={{ fontSize: 11 }}>
                IRN {summary.pending_irn_count} · FIR {summary.pending_qms_fir_count} · Plans{' '}
                {summary.plans_under_review_count}
              </Text>
            }
            footerLink={{
              label: 'Approvals',
              onClick: () => scrollTo('pending-approvals'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Plan items due soon"
            count={summary.plan_items_due_soon_count}
            icon={<ClockCircleOutlined />}
            urgency={summary.plan_items_due_soon_count > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: 'Plan items',
              onClick: () => scrollTo('plan-items'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Inspections in flight"
            count={summary.inspections_in_flight_count}
            icon={<FileTextOutlined />}
            urgency={summary.inspections_in_flight_count > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: 'Inspections',
              onClick: () => navigate('/qms/inspections'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Pass rate (30d)"
            count={0}
            valueDisplay={
              summary.inspection_pass_rate_30d == null
                ? '—'
                : `${summary.inspection_pass_rate_30d}%`
            }
            icon={<CheckCircleOutlined />}
            urgency={
              summary.inspection_pass_rate_30d != null && summary.inspection_pass_rate_30d < 90
                ? 'attention'
                : 'neutral'
            }
            footerLink={{
              label: 'Inspections',
              onClick: () => navigate('/qms/inspections'),
            }}
          />
        </Col>
      </ControlTowerMetricStrip>
    </div>
  );
};

export default QmsOutlookRibbon;
